import { Injectable } from '@angular/core';
import { WebClient } from '@injectables';
import { Device, NodeConnection } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { FlowNode } from './node-manager';
import { BehaviorSubject, tap } from 'rxjs';
import { FoblexNode } from './base-node/base-node';
import { BaseConnection } from './base-connection';


@Injectable({ providedIn: 'root' })
export class ConnectionManager {
    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly webClient: WebClient,
    ) {}

    public readonly device$ = this.fragQuery.device$;
    public readonly siteId$ = this.fragQuery.siteId$;
    public readonly nodeConnectionClient = this.webClient.model.nodeConnection;
    private connections: BaseConnection<NodeConnection.ServerSideMeta>[] = [];
    private connectionsSubject = new BehaviorSubject<BaseConnection<NodeConnection.ServerSideMeta>[]>([]);
    private readonly reassignSaveQueues = new Map<number, Promise<void>>();
    private readonly confirmedConnections = new Map<number, BaseConnection<NodeConnection.ServerSideMeta>>();
    public readonly connections$ = this.connectionsSubject.asObservable();

    public loadInitialConnections() {
        this.fragQuery.nodeConnections$.pipe(
            tap(connections => {
                this.connections = [];
                this.confirmedConnections.clear();
                if (!connections) {
                    this.connectionsSubject.next([]);
                    return;
                }
                connections.forEach(c => this.addConnection(c.toBaseConnection()));
            })
        ).subscribe();
    }

    public async addConnectionOnConnect(from: string, to: string) {
        if (!this.connections.find(c => c.from === from && c.to === to) && from && to) {
            const connection = new NodeConnection();
            connection.siteId = this.siteId$.value;
            connection.from = from;
            connection.to = to;

            await this.nodeConnectionClient.save(connection);
            const savedConnection = connection.toBaseConnection();
            this.connections.push(savedConnection);
            this.confirmedConnections.set(savedConnection.id, savedConnection);
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public async reassignConnection(from: string, to: string, newTo: string): Promise<void> {
        const oldConnectionIndex = this.connections.findIndex(
            (c) => c.from === from && c.to === to
        );

        if (oldConnectionIndex === -1) {
            return;
        }

        const previousConnection = this.connections[oldConnectionIndex];
        const reassignedConnection = new BaseConnection<NodeConnection.ServerSideMeta>(
            previousConnection.from,
            newTo,
            previousConnection.id,
            previousConnection.meta,
        );

        // Emit an immutable snapshot immediately so the canvas and comparison context both
        // reflect the reassigned upstream/downstream relationship.
        this.connections = this.connections.map((connection, index) =>
            index === oldConnectionIndex ? reassignedConnection : connection
        );
        this.connectionsSubject.next([...this.connections]);

        await this.queueReassignedConnectionSave(reassignedConnection);
    }

    private async queueReassignedConnectionSave(
        reassignedConnection: BaseConnection<NodeConnection.ServerSideMeta>,
    ): Promise<void> {
        const connectionId = reassignedConnection.id;
        const activeQueue = this.reassignSaveQueues.get(connectionId) ?? Promise.resolve();
        const saveQueue = activeQueue
            .catch(() => undefined)
            .then(async () => {
                const currentConnection = this.connections.find(
                    connection => connection.id === connectionId
                );
                if (!currentConnection) {
                    return;
                }

                try {
                    await this.nodeConnectionClient.save(
                        NodeConnection.fromBaseConnection(currentConnection),
                    );
                    this.confirmedConnections.set(connectionId, currentConnection);
                } catch (error) {
                    // Only roll back when the failed optimistic state is still the latest one,
                    // and always fall back to the last server-confirmed snapshot for this link.
                    const currentConnectionIndex = this.connections.findIndex(
                        connection => connection === currentConnection
                    );
                    if (currentConnectionIndex !== -1) {
                        const confirmedConnection = this.confirmedConnections.get(connectionId);
                        this.connections = this.connections.map((connection, index) =>
                            index === currentConnectionIndex && confirmedConnection
                                ? confirmedConnection
                                : connection
                        );
                        this.connectionsSubject.next([...this.connections]);
                    }
                    throw error;
                }
            });

        this.reassignSaveQueues.set(connectionId, saveQueue);

        try {
            await saveQueue;
        } finally {
            if (this.reassignSaveQueues.get(connectionId) === saveQueue) {
                this.reassignSaveQueues.delete(connectionId);
            }
        }
    }

    private addConnection(connection: BaseConnection<NodeConnection.ServerSideMeta>): void {
        const exists = this.connections.some(n => n.id === connection.id);
        if (!exists) {
            this.connections.push(connection);
            this.confirmedConnections.set(connection.id, connection);
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public getConnections(): BaseConnection<NodeConnection.ServerSideMeta>[] {
        return [...this.connections];
    }

    public async RemoveConnectionOnNodeRemove(node: FlowNode) {
        const connections = this.getConnections()
        if(FoblexNode.isBaseNode(node) && connections){
            const connectionsToDelete = connections.filter(conn =>
                conn.from === node.outputId || conn.to === node.inputId
            );
    
            for (const conn of connectionsToDelete) {
                await this.nodeConnectionClient.delete(NodeConnection.fromBaseConnection(conn));
                this.connections = this.connections.filter(c => c.id !== conn.id);
                this.confirmedConnections.delete(conn.id);
            }
    
            this.connections = this.connections.filter(conn =>
                !(conn.from === node.outputId || conn.to === node.inputId)
            );
    
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public removeAllConnections(): void {
        this.connections = [];
        this.confirmedConnections.clear();
        this.connectionsSubject.next([]);
    }
}
